import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FilePlus2, Loader2, Wallet, CheckCircle2, AlertTriangle } from 'lucide-react';
import ManualInvoiceLineItem from './ManualInvoiceLineItem';
import SplitsSection from './SplitsSection';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtINR = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

// FIX 1 (Task 3) — derive GST supply type from supplier state vs place of supply.
const normState = (s) => String(s || '').trim().toLowerCase().replace(/^\s*\d{1,2}\s*[-:.]?\s*/, '').replace(/[^a-z0-9]/g, '');
const deriveSupply = (supplierState, placeOfSupply) => {
  const a = normState(supplierState);
  const b = normState(placeOfSupply);
  if (!a || !b) return null;
  return a === b ? 'intrastate' : 'interstate';
};

const blankItem = () => ({ description: '', destination: '', travelDates: '', quantity: 1, amount: 0, productType: '', hsnCode: '', referenceNumber: '', referenceLabel: '' });

// Session F follow-up — productTypes a voucher/visa-doc can autofill a
// reference for, per the user's explicit scope (Flight/Hotel/Visa/Activity
// only). Tours/Misc/Package have no voucher-type source and are
// intentionally excluded — guessing from an Activity voucher for "Tours"
// would silently conflate two distinct types (see Session F's
// costItems.category vs productType vocabulary decision in PRD.md).
const AUTOFILL_TYPES = ['Flight', 'Hotel', 'Visa', 'Activity'];

const initialForm = () => ({
  invoiceType: 'tax',
  invoiceNumber: '',
  invoiceDate: new Date().toISOString().slice(0, 10),
  dueDate: '',
  supplyType: 'interstate',
  placeOfSupply: '',
  isInternational: false,
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  clientAddress: '',
  clientGstNumber: '',
  items: [blankItem()],
  gstRate: 5,
  tcsRate: '',
  notes: '',
  // Session F (Task F2) — Accounts-only internal note, never rendered on
  // the client-facing PDF.
  internalNote: '',
  // Declared up-front so every bound input stays controlled from first render
  // (avoids the controlled→uncontrolled React warning on edit-mode hydration).
  gstOnServiceFee: false,
  serviceFee: '',
  termsAndConditions: '',
  // Phase C4 — invoice split. Empty array = single-recipient mode.
  splits: [],
});

const blankSplit = () => ({
  travellerName: '', panCard: '', ucc: '',
  amount: 0, saveToProfile: false,
});

// F#1 + F#4 — `editing` (when truthy) seeds the form from an existing
// invoice and switches the submit handler from POST /api/invoices/manual
// to PUT /api/invoices/{invoiceNumber}. The same dialog is now the single
// edit-anything surface for both manual and trip-based invoices.
export default function ManualInvoiceDialog({ open, onOpenChange, onCreated, editing }) {
  const { user } = useAuth();
  const canSeeInternalNote = user?.role === 'admin' || user?.role === 'accounts';
  const [form, setForm] = useState(initialForm());
  const [busy, setBusy] = useState(false);
  // Cached company settings so the "Apply Default Template" button can
  // populate the T&C textarea instantly without a network round-trip.
  const [defaultTnc, setDefaultTnc] = useState('');
  const [companyState, setCompanyState] = useState('');
  // BATCH 2 #5 — optional reason recorded in the tax-change audit trail on edit.
  const [editReason, setEditReason] = useState('');
  // BATCH 2 #1/#2 — money position for a trip-linked invoice being edited.
  const [recon, setRecon] = useState(null);

  // Snapshot of the invoice number at the moment the dialog opened in
  // edit mode — used as the URL key for PUT even if the user renumbers
  // mid-edit.
  const originalNumber = editing?.invoiceNumber;

  // Fetch default invoice T&C template from settings (invoice-specific
  // first, then fall back to the global itinerary default). Cached for
  // the lifetime of the open dialog.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/settings`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const s = r.data || {};
        setDefaultTnc(s.defaultInvoiceTermsAndConditions || s.defaultTermsAndConditions || '');
        setCompanyState(s.state || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  // Suggest a number whenever invoice type changes (and on first open).
  // Skipped in edit mode — never auto-rewrite an existing invoice number.
  useEffect(() => {
    if (!open || editing) return;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/invoices/suggest-number?invoice_type=${form.invoiceType}`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setForm((p) => (p.invoiceNumber ? p : { ...p, invoiceNumber: r.data.invoiceNumber }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, form.invoiceType, editing]);

  // Pre-fill the form when the dialog opens in edit mode. We reset the
  // form when the dialog closes to keep the next "new" invoice flow clean.
  useEffect(() => {
    if (open && editing) {
      const items = Array.isArray(editing.items) && editing.items.length > 0
        ? editing.items.map((it) => ({
            description: it.description || '',
            destination: it.destination || '',
            travelDates: it.travelDates || '',
            quantity: it.quantity || 1,
            amount: it.amount || 0,
            productType: it.productType || '',
            hsnCode: it.hsnCode || '',
            referenceNumber: it.referenceNumber || '',
            referenceLabel: it.referenceLabel || '',
          }))
        : [blankItem()];
      setForm({
        invoiceType: editing.invoiceType || 'tax',
        invoiceNumber: editing.invoiceNumber || '',
        invoiceDate: editing.invoiceDate || new Date().toISOString().slice(0, 10),
        dueDate: editing.dueDate || '',
        supplyType: editing.supplyType || 'interstate',
        placeOfSupply: editing.placeOfSupply || '',
        isInternational: !!editing.isInternational,
        clientName: editing.client?.name || '',
        clientEmail: editing.client?.email || '',
        clientPhone: editing.client?.phone || '',
        clientAddress: editing.client?.address || '',
        clientGstNumber: editing.client?.gstNumber || '',
        items,
        gstRate: editing.gstRate ?? 5,
        tcsRate: editing.tcsRate == null ? '' : String(editing.tcsRate),
        notes: editing.notes || '',
        internalNote: editing.internalNote || '',
        // F#5 + F#8 — seed agency mode + T&C from the saved invoice.
        gstOnServiceFee: !!editing.gstOnServiceFee,
        serviceFee: editing.serviceFee == null || editing.serviceFee === 0 ? '' : String(editing.serviceFee),
        termsAndConditions: editing.termsAndConditions || '',
        splits: Array.isArray(editing.splits) ? editing.splits.map((s) => ({
          travellerName: s.travellerName || '',
          panCard: s.panCard || '',
          ucc: s.ucc || '',
          amount: s.amount || 0,
          saveToProfile: false,
        })) : [],
      });
    } else if (!open) {
      // Defer reset until close so re-opening for "new" starts fresh.
      setForm(initialForm());
    }
  }, [open, editing]);

  // BATCH 2 — on entering edit mode, clear the reason field and (for a
  // trip-linked invoice) load its payment reconciliation so the editor sees
  // what's already been received/invoiced.
  useEffect(() => {
    if (!(open && editing)) { setEditReason(''); setRecon(null); return; }
    setEditReason('');
    const tid = editing.tripId || editing.displayTripId;
    if (!tid) { setRecon(null); return; }
    let alive = true;
    axios.get(`${BACKEND_URL}/api/invoices/reconciliation/${encodeURIComponent(tid)}`, { withCredentials: true })
      .then((r) => { if (alive) setRecon(r.data); })
      .catch(() => { if (alive) setRecon(null); });
    return () => { alive = false; };
  }, [open, editing]);

  const updateField = useCallback((key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
  }, []);

  // Session F follow-up — PNR/HCN/Visa-number/Activity-reference autofill.
  // Only fills when the trip has EXACTLY ONE voucher of that type; otherwise
  // stays blank for manual entry. Never overwrites a value the agent already
  // has in the field (checked at write time, in case they typed while the
  // request was in flight).
  const tryAutofillReference = useCallback(async (idx, productType, tripIdForAutofill) => {
    if (!tripIdForAutofill || !AUTOFILL_TYPES.includes(productType)) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/invoices/reference-autofill`, {
        params: { tripId: tripIdForAutofill, productType }, withCredentials: true,
      });
      if (r.data?.referenceNumber) {
        setForm((p) => {
          const next = p.items.slice();
          if (next[idx] && !next[idx].referenceNumber) {
            next[idx] = { ...next[idx], referenceNumber: r.data.referenceNumber };
          }
          return { ...p, items: next };
        });
      }
    } catch { /* silent — manual entry remains available regardless */ }
  }, []);

  // Autofill any already-hydrated items missing a reference on dialog open.
  useEffect(() => {
    if (!(open && editing)) return;
    const tid = editing.tripId || editing.displayTripId;
    if (!tid) return;
    (editing.items || []).forEach((it, idx) => {
      if (!it.referenceNumber && AUTOFILL_TYPES.includes(it.productType)) {
        tryAutofillReference(idx, it.productType, tid);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  // FIX 1 — auto-derive supply type whenever the place of supply (or the
  // agency state) resolves to a definite intra/inter-state. Keeps the UI from
  // ever submitting a contradictory supplyType (the backend would 400 it).
  const derivedSupply = useMemo(
    () => deriveSupply(companyState, form.placeOfSupply),
    [companyState, form.placeOfSupply],
  );
  useEffect(() => {
    if (derivedSupply && derivedSupply !== form.supplyType) {
      setForm((p) => ({ ...p, supplyType: derivedSupply }));
    }
  }, [derivedSupply, form.supplyType]);

  const updateItemField = useCallback((idx, field, value) => {
    const numeric = field === 'amount' || field === 'quantity';
    setForm((p) => {
      const next = p.items.slice();
      next[idx] = { ...next[idx], [field]: numeric ? Number(value) || 0 : value };
      return { ...p, items: next };
    });
    if (field === 'productType') {
      tryAutofillReference(idx, value, editing?.tripId || editing?.displayTripId);
    }
  }, [editing, tryAutofillReference]);

  const addItem = useCallback(() => {
    setForm((p) => ({ ...p, items: p.items.concat(blankItem()) }));
  }, []);

  const removeItem = useCallback((idx) => {
    setForm((p) => {
      if (p.items.length <= 1) return p;
      const next = p.items.slice();
      next.splice(idx, 1);
      return { ...p, items: next };
    });
  }, []);

  // Phase C4 — split handlers
  const addSplit = useCallback(() => {
    setForm((p) => ({ ...p, splits: [...(p.splits || []), blankSplit()] }));
  }, []);
  const updateSplit = useCallback((idx, key, value) => {
    setForm((p) => {
      const next = (p.splits || []).slice();
      next[idx] = { ...next[idx], [key]: value };
      return { ...p, splits: next };
    });
  }, []);
  const removeSplit = useCallback((idx) => {
    setForm((p) => {
      const next = (p.splits || []).slice();
      next.splice(idx, 1);
      return { ...p, splits: next };
    });
  }, []);

  const totals = useMemo(() => {
    const baseCost = form.items.reduce(
      (sum, it) => sum + (Number(it.amount) || 0) * (Number(it.quantity) || 1),
      0
    );
    // F#5 — GST base flips to serviceFee in agency mode.
    const serviceFeeNum = Number(form.serviceFee) || 0;
    const gstBase = form.gstOnServiceFee ? serviceFeeNum : baseCost;
    const gstAmount = (gstBase * Number(form.gstRate || 0)) / 100;
    const tcsRateNum = form.tcsRate === '' ? 0 : Number(form.tcsRate);
    // TCS always on full base (the actual remittance amount).
    const tcsAmount = (baseCost * tcsRateNum) / 100;
    const subtotal = baseCost + serviceFeeNum + gstAmount;
    return {
      baseCost,
      serviceFee: serviceFeeNum,
      gstBase,
      gstAmount,
      tcsRateNum,
      tcsAmount,
      subtotal,
      total: subtotal + tcsAmount,
    };
  }, [form.items, form.gstRate, form.tcsRate, form.gstOnServiceFee, form.serviceFee]);

  // TCS rate is one of 0 / 2 / 20. A stored value outside that set (e.g. the
  // retired 5% rate) is "legacy" — we still surface it so totals stay correct
  // but flag it for the operator to update.
  const isLegacyTcs = form.tcsRate !== '' && form.tcsRate != null
    && !['0', '2', '20'].includes(String(form.tcsRate));

  // Phase C4 — split sum-check info, pre-computed so the JSX stays a plain
  // div (no inline IIFE — that pattern tripped the visual-edits babel
  // plugin's MemberExpression analyzer in iter36).
  const splitSumInfo = useMemo(() => {
    const rows = form.splits || [];
    const sum = rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
    const diff = sum - totals.baseCost;
    const fmt = (v) => v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    return {
      sumLabel: fmt(sum),
      baseLabel: fmt(totals.baseCost),
      diffLabel: fmt(Math.abs(diff)),
      ok: Math.abs(diff) < 1,
    };
  }, [form.splits, totals.baseCost]);

  // Pull items out of `form` so the babel visual-edits plugin doesn't recurse
  // on the form.items.map() member-expression chain (a known plugin bug).
  const items = form.items;
  const renderedItems = items.map((it, idx) => (
    <ManualInvoiceLineItem
      key={idx}
      index={idx}
      description={it.description}
      destination={it.destination}
      quantity={it.quantity}
      amount={it.amount}
      productType={it.productType}
      hsnCode={it.hsnCode}
      referenceNumber={it.referenceNumber}
      referenceLabel={it.referenceLabel}
      onChangeField={updateItemField}
      onRemove={removeItem}
      canRemove={items.length > 1}
    />
  ));

  const submit = async () => {
    if (!form.clientName.trim()) return toast.error('Client name is required');
    if (!form.invoiceNumber.trim()) return toast.error('Invoice number is required');
    const invalid = items.some((it) => !it.description.trim() || it.amount <= 0);
    if (invalid) return toast.error('All items must have description and amount');

    setBusy(true);
    try {
      const cleanSplits = (form.splits || [])
        .filter((s) => (s.travellerName || '').trim() && Number(s.amount) > 0)
        .map((s) => ({
          travellerName: s.travellerName.trim(),
          panCard: (s.panCard || '').trim().toUpperCase(),
          ucc: s.ucc || '',
          amount: Number(s.amount) || 0,
          saveToProfile: !!s.saveToProfile,
        }));
      // Guard rail: split totals must match invoice base cost (within ₹1).
      if (cleanSplits.length > 0) {
        const splitSum = cleanSplits.reduce((sum, s) => sum + s.amount, 0);
        if (Math.abs(splitSum - totals.baseCost) > 1) {
          return toast.error(
            `Split totals (₹${splitSum.toFixed(0)}) don't match invoice base (₹${totals.baseCost.toFixed(0)})`,
          );
        }
      }
      const payload = {
        ...form,
        gstRate: Number(form.gstRate) || 0,
        tcsRate: form.tcsRate === '' ? null : totals.tcsRateNum,
        serviceFee: form.serviceFee === '' ? 0 : Number(form.serviceFee) || 0,
        gstOnServiceFee: !!form.gstOnServiceFee,
        termsAndConditions: form.termsAndConditions || '',
        splits: cleanSplits.length > 0 ? cleanSplits : null,
      };
      let saved;
      if (editing && originalNumber) {
        // F#1 EDIT — PUT to /api/invoices/{originalNumber}. Note: the
        // backend recomputes tax + subtotal + total from items+rates so
        // we don't need to forward the computed totals.
        const url = `${BACKEND_URL}/api/invoices/${encodeURIComponent(originalNumber)}`;
        const r = await axios.put(url, { ...payload, reason: editReason || '' }, { withCredentials: true });
        saved = r.data;
        toast.success(`Invoice ${saved.invoiceNumber} updated`);
      } else {
        const r = await axios.post(`${BACKEND_URL}/api/invoices/manual`, payload, { withCredentials: true });
        saved = r.data;
        toast.success(`${form.invoiceType === 'proforma' ? 'Proforma' : 'Tax'} invoice ${saved.invoiceNumber} created`);
      }
      onOpenChange(false);
      if (onCreated) onCreated(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editing ? 'Failed to update invoice' : 'Failed to create invoice'));
    } finally {
      setBusy(false);
    }
  };

  const itemsBlock = renderedItems;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePlus2 className="w-5 h-5 text-teal-600" />
            {editing ? `Edit Invoice ${editing.invoiceNumber}` : 'Manual Invoice (No Trip Required)'}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? 'Edit any field on this invoice. Totals will be recomputed from items + GST + TCS.'
              : 'Create an ad-hoc invoice for any client — outsider billings, refunds, agency commissions.'}
          </DialogDescription>
        </DialogHeader>

        {/* BATCH 2 #1/#2 — payment position for a trip-linked invoice on edit. */}
        {editing && recon && (
          <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 mb-1" data-testid="manual-invoice-recon">
            <div className="flex items-center gap-1.5 mb-2">
              <Wallet className="h-3.5 w-3.5 text-teal-600" />
              <span className="text-xs font-semibold text-slate-700">Payment position · {recon.displayTripId}</span>
            </div>
            <div className="grid grid-cols-4 gap-3 text-center">
              <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Total</p><p className="text-sm font-semibold text-slate-900">{fmtINR(recon.payable)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Received</p><p className={`text-sm font-semibold ${recon.received > 0 ? 'text-emerald-700' : 'text-slate-900'}`}>{fmtINR(recon.received)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wide text-slate-400">Invoiced</p><p className="text-sm font-semibold text-slate-900">{fmtINR(recon.invoiced)}</p></div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{recon.overpaid ? 'Overpaid' : 'Outstanding'}</p>
                <p className={`text-sm font-semibold ${recon.overpaid ? 'text-amber-700' : (recon.outstanding > 0 ? 'text-rose-700' : 'text-emerald-700')}`}>{fmtINR(recon.overpaid ? recon.overpaidBy : recon.outstanding)}</p>
              </div>
            </div>
            {recon.overpaid ? (
              <div className="mt-2.5 flex items-start gap-1.5 text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-2.5 py-2" data-testid="manual-invoice-recon-overpaid">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Overpaid, needs review.
              </div>
            ) : recon.status === 'paid' ? (
              <div className="mt-2.5 flex items-center gap-1.5 text-xs text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-lg px-2.5 py-2" data-testid="manual-invoice-recon-paid">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Received {fmtINR(recon.received)} · Status: PAID.
              </div>
            ) : null}
          </div>
        )}

        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Invoice Type</Label>
              <Select value={form.invoiceType} onValueChange={(v) => setForm((p) => ({ ...p, invoiceType: v, invoiceNumber: '' }))}>
                <SelectTrigger className="mt-1" data-testid="manual-invoice-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tax">Tax Invoice</SelectItem>
                  <SelectItem value="proforma">Proforma Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Invoice Number *</Label>
              <Input value={form.invoiceNumber} onChange={(e) => updateField('invoiceNumber', e.target.value)} className="mt-1 font-mono" data-testid="manual-invoice-number" />
            </div>
            <div>
              <Label className="text-xs">Invoice Date</Label>
              <Input type="date" value={form.invoiceDate} onChange={(e) => updateField('invoiceDate', e.target.value)} className="mt-1" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Client / Bill To</h3>
              {/* Phase 17.2 #8 — pre-fill from an existing traveller's
                  corporate GST profile. Type the UCC (TRVGT_xxx) and click
                  "Pull" to hydrate name + email + phone + GST + address. */}
              <PrefillFromTraveller updateField={updateField} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Client name *" value={form.clientName} onChange={(e) => updateField('clientName', e.target.value)} data-testid="manual-client-name" />
              <Input placeholder="Email" value={form.clientEmail} onChange={(e) => updateField('clientEmail', e.target.value)} />
              <Input placeholder="Phone" value={form.clientPhone} onChange={(e) => updateField('clientPhone', e.target.value)} />
              <Input
                placeholder="GST Number (optional)"
                value={form.clientGstNumber}
                onChange={(e) => updateField('clientGstNumber', (e.target.value || '').toUpperCase())}
                className="font-mono uppercase"
                maxLength={15}
                data-testid="manual-client-gst"
              />
            </div>
            <Input placeholder="Address" value={form.clientAddress} onChange={(e) => updateField('clientAddress', e.target.value)} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Line Items</h3>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
              </Button>
            </div>
            {itemsBlock}
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Supply Type</Label>
              <Select value={form.supplyType} onValueChange={(v) => updateField('supplyType', v)} disabled={!!derivedSupply}>
                <SelectTrigger className="mt-1" data-testid="manual-supply-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interstate">Interstate (IGST)</SelectItem>
                  <SelectItem value="intrastate">Intrastate (CGST+SGST)</SelectItem>
                </SelectContent>
              </Select>
              {derivedSupply ? (
                <p className="text-[11px] text-teal-700 mt-1" data-testid="manual-supply-auto">
                  Auto-derived from place of supply vs {companyState}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-xs">GST %</Label>
              <Input type="number" step="0.01" value={form.gstRate ?? ''} onChange={(e) => updateField('gstRate', e.target.value)} className="mt-1" data-testid="manual-gst-rate" />
            </div>
            <div>
              <Label className="text-xs">TCS Rate</Label>
              <Select value={form.tcsRate} onValueChange={(v) => updateField('tcsRate', v)}>
                <SelectTrigger className="mt-1" data-testid="manual-tcs-rate"><SelectValue placeholder="Select TCS rate" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Domestic)</SelectItem>
                  <SelectItem value="2">2% (International, PAN present)</SelectItem>
                  <SelectItem value="20">20% (International, no PAN)</SelectItem>
                  {isLegacyTcs ? (
                    <SelectItem value={String(form.tcsRate)} data-testid="manual-tcs-legacy-option">
                      {form.tcsRate}% — Legacy (update recommended)
                    </SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
              {isLegacyTcs ? (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="manual-tcs-legacy-warning">
                  Legacy rate — update recommended
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-xs">Place of Supply</Label>
              <Input value={form.placeOfSupply} onChange={(e) => updateField('placeOfSupply', e.target.value)} className="mt-1" />
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm" data-testid="manual-totals">
            <div className="flex justify-between"><span className="text-slate-600">Base</span><span className="font-mono">₹ {totals.baseCost.toLocaleString('en-IN')}</span></div>
            {form.gstOnServiceFee && totals.serviceFee > 0 && (
              <div className="flex justify-between"><span className="text-slate-600">Service Fee</span><span className="font-mono">₹ {totals.serviceFee.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
            )}
            <div className="flex justify-between">
              <span className="text-slate-600">
                GST ({form.gstRate}%){form.gstOnServiceFee ? ' on Service Fee' : ''}
              </span>
              <span className="font-mono">₹ {totals.gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
            </div>
            {totals.tcsRateNum > 0 && <div className="flex justify-between"><span className="text-slate-600">TCS ({totals.tcsRateNum}%)</span><span className="font-mono">₹ {totals.tcsAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>}
            <div className="flex justify-between border-t border-teal-200 mt-1 pt-1 font-semibold text-teal-800"><span>Total</span><span className="font-mono">₹ {totals.total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
          </div>

          {/* F#5 — Agency / Service-Fee mode. When enabled, GST applies only
              to the entered service fee; the items still show full cost. */}
          <div className="grid grid-cols-3 gap-3 items-end" data-testid="manual-service-fee-section">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer mt-6 col-span-1" data-testid="manual-gst-on-service-fee-toggle">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={!!form.gstOnServiceFee}
                onChange={(e) => updateField('gstOnServiceFee', e.target.checked)}
              />
              GST only on service fee
            </label>
            <div className="col-span-2">
              <Label className="text-xs">Service Fee (₹)</Label>
              <Input
                type="number"
                min="0"
                value={form.serviceFee ?? ''}
                onChange={(e) => updateField('serviceFee', e.target.value)}
                placeholder="Agency commission amount"
                className="mt-1"
                disabled={!form.gstOnServiceFee}
                data-testid="manual-service-fee-input"
              />
            </div>
          </div>

          <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => updateField('notes', e.target.value)} />

          {/* Session F (Task F2) — Accounts-only internal note. Never sent
              to the client-facing PDF (InvoicePreview.js has no UI for it)
              and hidden here for any role other than admin/accounts. */}
          {canSeeInternalNote && (
            <div data-testid="manual-internal-note-section">
              <Label className="text-xs">Internal Note <span className="text-slate-400">(Admin/Accounts only — never shown to client)</span></Label>
              <textarea
                className="w-full min-h-[70px] rounded-md border border-slate-200 bg-white p-3 text-sm leading-relaxed mt-1"
                placeholder="Internal accounting note (e.g. reconciliation flags, follow-up reminders)…"
                value={form.internalNote}
                onChange={(e) => updateField('internalNote', e.target.value)}
                data-testid="manual-internal-note-textarea"
              />
            </div>
          )}

          {/* Phase C4 — Split among multiple travellers (per-PAN). Extracted
              to its own component so the visual-edits babel plugin doesn't
              recurse on the large render tree (iter36 RangeError fix). */}
          <SplitsSection
            splits={form.splits}
            onAdd={addSplit}
            onUpdate={updateSplit}
            onRemove={removeSplit}
            sumInfo={splitSumInfo}
          />

          {/* F#8 — Editable T&C template. Empty on create = backend seeds
              from companySettings (invoice-specific first, then global). */}
          <div data-testid="manual-tnc-section">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Terms & Conditions (rendered in invoice footer)</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs"
                disabled={!defaultTnc}
                data-testid="manual-apply-default-tnc-btn"
                onClick={() => {
                  if (!defaultTnc) {
                    toast.error('No default Invoice T&C set in Settings → Company Details');
                    return;
                  }
                  if (
                    form.termsAndConditions &&
                    !window.confirm('Replace the current Terms & Conditions with the default template?')
                  ) return;
                  updateField('termsAndConditions', defaultTnc);
                  toast.success('Default Invoice T&C applied');
                }}
              >
                Apply Default Template
              </Button>
            </div>
            <textarea
              className="w-full min-h-[120px] rounded-md border border-slate-200 bg-white p-3 text-sm font-mono leading-relaxed"
              placeholder="(Leave blank to use the default template from Settings → Company Details → Default Invoice T&C.)"
              value={form.termsAndConditions}
              onChange={(e) => updateField('termsAndConditions', e.target.value)}
              data-testid="manual-tnc-textarea"
            />
          </div>
        </div>

        {editing && (
          <div className="pt-2 border-t border-slate-100">
            <Label className="text-xs">Reason for change <span className="text-slate-400">(optional · recorded in the tax audit trail)</span></Label>
            <Input
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="e.g. corrected taxable amount, added service fee…"
              className="mt-1"
              data-testid="manual-invoice-reason"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="manual-invoice-submit">
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {editing ? 'Saving…' : 'Creating…'}</>
            ) : (
              editing ? 'Save Changes' : `Create ${form.invoiceType === 'proforma' ? 'Proforma' : 'Tax'} Invoice`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 17.2 #8 — auto-fill helper. Typeahead-lite for the operator —
// type a UCC (e.g. `TRVGT_026`) and click Pull. We hit GET /api/clients/{ucc}
// and hydrate the Client section with the traveller's name + email + phone
// + corporate-GST + corporate-address (or personal address as fallback).
function PrefillFromTraveller({ updateField }) {
  const [ucc, setUcc] = useState('');
  const [busy, setBusy] = useState(false);
  const onPull = async () => {
    const u = (ucc || '').trim().toUpperCase();
    if (!u) return;
    setBusy(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/${u}`, { withCredentials: true });
      const c = r.data || {};
      const billingName = c.companyName || c.fullName || '';
      updateField('clientName', billingName);
      updateField('clientEmail', c.email || '');
      updateField('clientPhone', c.phone || '');
      // Corporate GST + address win over personal when present.
      updateField('clientGstNumber', (c.companyGstNumber || '').toUpperCase());
      updateField('clientAddress', c.companyAddress || c.address || '');
      toast.success(c.companyName ? `Filled corporate details — ${c.companyName}` : `Filled details for ${c.fullName}`);
      setUcc('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Traveller not found');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="inline-flex items-center gap-1.5">
      <Input
        value={ucc}
        onChange={(e) => setUcc(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onPull(); } }}
        placeholder="UCC e.g. TRVGT_026"
        data-testid="manual-prefill-ucc"
        className="h-7 text-xs w-36 font-mono uppercase"
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onPull}
        disabled={busy || !ucc.trim()}
        data-testid="manual-prefill-pull"
        className="h-7 text-xs"
      >
        {busy ? 'Pulling…' : 'Pull'}
      </Button>
    </div>
  );
}
